import { PhoneNumber } from "../misc/PhoneNumber";
import { Clock, Globe, MapPin, Phone } from "lucide-react";

import { useRecordContext, useTranslate } from "ra-core";

import { EditButton } from "@/components/admin/edit-button";

import { DeleteButton } from "@/components/admin/delete-button";

import { ShowButton } from "@/components/admin/show-button";

import { UrlField } from "@/components/admin/url-field";

import { AsideSection } from "../misc/AsideSection";

import type { Company } from "../types";

import { CompanyTagsEdit } from "./CompanyTagsEdit";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();

  const translate = useTranslate();

  if (!record) {
    return null;
  }

  return (
    <div className="w-full min-w-0 lg:w-80 lg:min-w-80 space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" ? (
          <EditButton label={translate("resources.companies.action.edit")} />
        ) : (
          <ShowButton label={translate("resources.companies.action.show")} />
        )}
      </div>

      <CompanyInfo record={record} />

      <CompanySpecialization record={record} />

      <OperationalInfo record={record} />

      <AddressInfo record={record} />

      <AdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  if (!record.website && !record.phone_number) {
    return null;
  }

  return (
    <AsideSection title="Контактные данные">
      {record.phone_number && (
        <div className="flex flex-row items-center gap-2 min-h-[24px]">
          <Phone className="w-4 h-4 text-muted-foreground" />

          <PhoneNumber source="phone_number" />
        </div>
      )}

      {record.website && (
        <div className="flex flex-row items-center gap-2 min-h-[24px]">
          <Globe className="w-4 h-4 text-muted-foreground" />

          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
    </AsideSection>
  );
};

export const CompanySpecialization = ({ record }: { record: Company }) => {
  return (
    <AsideSection title="Теги">
      <CompanyTagsEdit record={record} />
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  const parts = [
    record.address,
    record.city,
    record.state_abbr,
    record.zipcode,
    record.country,
  ].filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  return (
    <AsideSection title="Адрес">
      <div className="flex items-start gap-2">
        <MapPin className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />

        <div className="text-sm">{parts.join(", ")}</div>
      </div>
    </AsideSection>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  if (!record.description) {
    return null;
  }

  return (
    <AsideSection title="Заметка">
      <p className="whitespace-pre-wrap text-sm">{record.description}</p>
    </AsideSection>
  );
};

export const OperationalInfo = ({ record }: { record: Company }) => {
  const hasCommunicationHours = !!record.communication_hours?.trim();

  const hasLeadTime = record.average_lead_time_days != null;

  if (!hasCommunicationHours && !hasLeadTime) {
    return null;
  }

  return (
    <AsideSection title="Рабочая информация">
      {hasCommunicationHours && (
        <div className="flex items-start gap-2 min-h-[24px]">
          <Clock className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />

          <div className="text-sm">{record.communication_hours}</div>
        </div>
      )}

      {hasLeadTime && (
        <div className="flex items-start gap-2 min-h-[24px]">
          <Clock className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />

          <div className="text-sm">
            Средний срок:{" "}
            <span className="font-medium">
              {record.average_lead_time_days} дн.
            </span>
          </div>
        </div>
      )}
    </AsideSection>
  );
};
